import React, { useState, useEffect, useRef, useCallback } from "react";
import {
    Layout, Table, Input, Button, Form, Space, Row, Col,
    Breadcrumb, Modal, message, Typography, Tooltip, Switch
} from "antd";
import Headerdiv from "../../layout/header";
import Footerdiv from "../../layout/footer";
import {
    PlusOutlined, EditOutlined, HomeOutlined, TagsOutlined,
    SearchOutlined, CheckCircleFilled, CloseCircleFilled,
    FilterOutlined, ClearOutlined
} from "@ant-design/icons";
import api from "../../../services/axiosInstance";
import Highlighter from "react-highlight-words";
import "../../../styles/fluidDesign.css";

const { Content } = Layout;

const ManageInventoryCategory = () => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [currentItem, setCurrentItem] = useState(null);
    const [addForm] = Form.useForm();
    const [editForm] = Form.useForm();

    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [isAddModalVisible, setIsAddModalVisible] = useState(false);    const [searchText, setSearchText]       = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef(null);

    // Top filter bar state (server-side search)
    const [search, setSearch] = useState('');
    const [applied, setApplied] = useState({});

    const handleSearch = (selectedKeys, confirm, dataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };
    const handleReset = (clearFilters) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = (dataIndex) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button type="primary" onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                        icon={<SearchOutlined />} size="small" style={{ width: 90 }}>Search</Button>
                    <Button onClick={() => clearFilters && handleReset(clearFilters)} size="small" style={{ width: 90 }}>Reset</Button>
                    <Button type="link" size="small" onClick={() => { confirm({ closeDropdown: false }); setSearchText(selectedKeys[0]); setSearchedColumn(dataIndex); }}>Filter</Button>
                    <Button type="link" size="small" onClick={() => close()}>close</Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />,
        onFilter: (value, record) => record[dataIndex]?.toString().toLowerCase().includes(value.toLowerCase()),
        filterDropdownProps: { onOpenChange(open) { if (open) setTimeout(() => searchInput.current?.select(), 100); } },
        render: (text) => searchedColumn === dataIndex ? (
            <Highlighter
                highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                searchWords={[searchText]} autoEscape
                textToHighlight={text ? text.toString() : ''}
            />
        ) : text,
    });

    const fetchCategories = useCallback(async (page = 1, pageSize = 10, filters = {}) => {
        try {
            setLoading(true);
            const res = await api.get(`/inventory/category/list.php`, {
                params: {
                    page,
                    limit: pageSize,
                    search: filters.search || '',
                },
            });
            setCategories(res.data.dataset.map(item => ({
                icId: item.ic_id,
                icName: item.ic_name,
                icActive: item.ic_active,
            })));
            setPagination({
                current: res.data.pagination?.current_page || 1,
                pageSize: res.data.pagination?.per_page || 10,
                total: res.data.pagination?.total_records || 0,
            });
        } catch {
            message.error("Failed to fetch inventory categories.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchCategories(1, 10, {}); }, [fetchCategories]);
    const handleTableChange = (pg) => fetchCategories(pg.current, pg.pageSize, applied);

    const handleFilter = () => {
        const filters = { search: search.trim() };
        setApplied(filters);
        fetchCategories(1, pagination.pageSize, filters);
    };

    const handleClear = () => {
        setSearch('');
        setApplied({});
        fetchCategories(1, pagination.pageSize, {});
    };

    const handleAdd = async (values) => {
        try {
            setLoading(true);
            const res = await api.post('/inventory/category/add.php', {
                ic_name: values.icName,
            });
            if (res.data.status === 'success') {
                message.success(res.data.message || "Category added successfully.");
                setIsAddModalVisible(false);
                fetchCategories(pagination.current, pagination.pageSize, applied);
                addForm.resetFields();
            } else throw new Error(res.data.message);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to add category.");
        } finally { setLoading(false); }
    };

    const openEdit = (record) => {
        setCurrentItem(record);
        editForm.setFieldsValue({
            icName: record.icName,
        });
        setIsModalVisible(true);
    };

    const handleEdit = async (values) => {
        try {
            setLoading(true);
            const res = await api.post('/inventory/category/edit.php', {
                ic_id: currentItem.icId,
                ic_name: values.icName,
            });
            if (res.data.status === 'success') {
                message.success(res.data.message || "Category updated successfully.");
                setIsModalVisible(false);
                fetchCategories(pagination.current, pagination.pageSize, applied);
                editForm.resetFields();
            } else throw new Error(res.data.message);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to update category.");
        } finally { setLoading(false); }
    };

    const handleToggle = async (record, checked) => {
        try {
            const res = await api.post('/inventory/category/toggle.php', {
                ic_id: record.icId,
                ic_active: checked ? 1 : 0,
            });
            if (res.data.status === 'success') {
                message.success(res.data.message);
                fetchCategories(pagination.current, pagination.pageSize, applied);
            } else throw new Error(res.data.message);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to update status.");
        }
    };

    const columns = [
        {
            title: 'ID', dataIndex: 'icId', key: 'icId', width: 80,
        },
        {
            title: 'Category Name', dataIndex: 'icName', key: 'icName',
            sorter: (a, b) => a.icName.localeCompare(b.icName),
            ...getColumnSearchProps('icName'),
        },
        {
            title: 'Status', dataIndex: 'icActive', key: 'icActive',
            align: 'center', width: 120,
            render: (val) => val == 1
                ? <Tooltip title="Active"><Space size={4}><CheckCircleFilled style={{ color: 'green', fontSize: 16 }} /><Typography.Text style={{ color: 'green' }}>Active</Typography.Text></Space></Tooltip>
                : <Tooltip title="Inactive"><Space size={4}><CloseCircleFilled style={{ color: 'red', fontSize: 16 }} /><Typography.Text style={{ color: 'red' }}>Inactive</Typography.Text></Space></Tooltip>,
        },
        {
            title: 'Action', key: 'action', align: 'center', fixed: 'right', width: 120,
            render: (_, record) => (
                <Space>
                    <Tooltip title="Edit Category">
                        <Button type="text" icon={<EditOutlined />} onClick={() => openEdit(record)} />
                    </Tooltip>
                    <Tooltip title={record.icActive == 1 ? "Deactivate" : "Activate"}>
                        <Switch
                            size="small"
                            checked={record.icActive == 1}
                            onChange={(checked) => handleToggle(record, checked)}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <Layout className="fluid-page">
            <Headerdiv />

            <div className="fluid-page-header">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <Row align="bottom" justify="space-between" gutter={[16, 12]}>
                    <Col>
                                                    <Breadcrumb
                                className="fluid-breadcrumb"
                                items={[
                                    { href: '/dash', title: <HomeOutlined /> },
                                    { href: '/inventory-items', title: 'Inventory' },
                                    { title: 'Categories' },
                                ]}
                            />
                        <h1 className="fluid-page-title">
                            <TagsOutlined /> Inventory Categories
                        </h1>
                        <p className="fluid-page-subtitle">Configure category classifications for stock items</p>
                    </Col>
                    <Col xs={24} md={12} style={{ textAlign: 'right' }}>
                        <div className="hero-actions" style={{ marginTop: 0, justifyContent: 'flex-end' }}>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => setIsAddModalVisible(true)}
                                className="hero-action-btn"
                            >
                                Add New Category
                            </Button>
                        </div>
                    </Col>
                </Row>
            </div>

            <Content className="fluid-content">
                <div className="fluid-card">
                    <div className="fluid-filter-compact" style={{ marginBottom: 12 }}>
                        <Row gutter={16} align="bottom" className="fluid-filter-row">
                            <Col xs={24} sm={12} md={8} lg={6}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Search:</div>
                                <Input
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    onPressEnter={handleFilter}
                                    placeholder="Category name"
                                    prefix={<SearchOutlined />}
                                    allowClear
                                />
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={4}>
                                <div className="fluid-filter-compact-actions">
                                    <Button type="primary" icon={<FilterOutlined />} onClick={handleFilter} className="fluid-btn-primary">
                                        Apply
                                    </Button>
                                    <Button icon={<ClearOutlined />} onClick={handleClear} className="fluid-btn-secondary">
                                        Clear
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </div>

                    <div className="fluid-table">
                        <Table
                            size="small"
                            columns={columns}
                            dataSource={categories}
                            rowKey="icId"
                            pagination={{
                                current: pagination.current,
                                pageSize: pagination.pageSize,
                                total: pagination.total,
                                showSizeChanger: true,
                                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                            }}
                            onChange={handleTableChange}
                            loading={loading}
                            scroll={{ x: 600 }}
                        />
                    </div>
                </div>
            </Content>

            <Footerdiv />

            <Modal
                title="Add New Category"
                open={isAddModalVisible}
                onCancel={() => { setIsAddModalVisible(false); addForm.resetFields(); }}
                footer={null}
                width={600}
                destroyOnClose
            >
                <Form form={addForm} layout="vertical" onFinish={handleAdd}>
                    <Form.Item label="Category Name" name="icName" rules={[{ required: true, message: 'Category name is required' }]}>
                        <Input placeholder="Fertilizers, Tools, Seeds..." />
                    </Form.Item>
                    <Row justify="end">
                        <Form.Item style={{ marginBottom: 0 }}>
                            <Space>
                                <Button danger onClick={() => { setIsAddModalVisible(false); addForm.resetFields(); }}>Cancel</Button>
                                <Button type="primary" htmlType="submit" icon={<PlusOutlined />} loading={loading}>Add Category</Button>
                            </Space>
                        </Form.Item>
                    </Row>
                </Form>
            </Modal>

            <Modal
                title="Edit Category Details"
                open={isModalVisible}
                onCancel={() => { setIsModalVisible(false); editForm.resetFields(); }}
                footer={null}
                width={500}
            >
                <Form form={editForm} onFinish={handleEdit} layout="vertical">
                    <Form.Item label="Category Name" name="icName" rules={[{ required: true, message: 'Category name is required' }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={loading}>Save Changes</Button>
                            <Button danger onClick={() => setIsModalVisible(false)}>Cancel</Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </Layout>
    );
};

export default ManageInventoryCategory;
